import styled from '@emotion/styled'
import { Theme } from 'bold-ui'
import { UseFieldProps, usePecField } from 'components/form/final-form/useField'
import { FormApi } from 'final-form'
import useMeasure from 'hooks/useMeasure'
import React, { PropsWithChildren } from 'react'
import { animated, useSpring } from 'react-spring'
import { v4 as uuidv4 } from 'uuid'

export interface RowType {
  _id: ID
}

export interface EditableFormListOptions<T extends RowType> extends UseFieldProps<T> {}

export function useEditableListField<T extends RowType>(options: EditableFormListOptions<T>) {
  const fieldProps = usePecField({ ...options })

  const handleSubmit = (formValues: T, formApi: FormApi) => {
    const value = { ...formValues, _id: uuidv4() }
    fieldProps.input.onChange([...fieldProps.input.value, value])
    fieldProps.input.onBlur() // usado pra marcar o field como touched no form externo
    formApi.getRegisteredFields().forEach((field) => formApi.resetFieldState(field))
    setTimeout(formApi.reset)
  }

  const handleRowChanged = (formValues: T) => {
    fieldProps.input.onChange(
      fieldProps.input.value.map((item) => {
        if (item._id === formValues._id) {
          return formValues
        }
        return item
      })
    )
    fieldProps.input.onBlur() // usado pra marcar o field como touched no form externo
  }

  const removeItem = (itemToRemove: T) => {
    fieldProps.input.onChange(fieldProps.input.value.filter((item) => item._id !== itemToRemove._id))
    fieldProps.input.onBlur() // usado pra marcar o field como touched no form externo
  }

  return { handleSubmit, handleRowChanged, removeItem, ...fieldProps }
}

interface EditableRowProps {
  editing: boolean
}

export const EditableRow = (props: PropsWithChildren<EditableRowProps>) => {
  const { children, editing } = props
  const [ref, { height }] = useMeasure()
  const springProps = useSpring({ height })

  return (
    <EditableRowWrapper editing={editing} style={springProps}>
      <div ref={ref}>{children}</div>
    </EditableRowWrapper>
  )
}

const EditableRowWrapper = animated(styled.div<EditableRowProps, Theme>`
  background-color: ${(props) => props.editing && props.theme.pallete.primary.c90};
  padding: 0 1rem;
  overflow: hidden;
  transition: background-color 0.4s ease-in-out;
`)

export const EditableList = styled.div<{}, Theme>`
  border: 1px solid ${(props) => props.theme.pallete.divider};
  border-radius: ${(props) => props.theme.radius.input}px;
  > div:not(:last-of-type) {
    border-bottom: 1px solid ${(props) => props.theme.pallete.divider};
  }
`
